import {
  Bitmap,
  BitmapData,
  Color,
  ColorMatrix,
  ColorMatrixFilter,
  MovieClip,
} from "../runtime";
import { EFFECT_SPECS, ITEM_PICKUPS, TRAIL_SHEETS } from "./data/effects";
import { SWORD_HUES } from "./data/swordColours";
import { EffectId } from "./ids";
import { SpriteLayer } from "./SpriteLayer";

/** Where a moving effect ended up this frame, for the collision pass. */
export interface MoveState {
  xx: number;
  yy: number;
  angle: number;
  scale: number;
  fading: boolean;
}

export class Effect extends MovieClip {
  readonly id: EffectId;
  fading = false;
  hitCD = 0;
  private readonly tileSize: number;
  private readonly layer: SpriteLayer;
  private readonly lifeLimit: number;
  private readonly fadeRate: number;
  private readonly animSpeed: number;
  private life = 0;
  private alphaCurrent = 100;
  private frame = 0;
  private frameCounter = 0;
  private frameLimit = 0;
  private action = 0;
  private animOffset = 0;
  private targetScale = 0;
  private targetAngle = 0;
  private moveSpeed = 0;
  private xSpeed = 0;
  private ySpeed = 0;

  constructor(
    type: EffectId = EffectId.Dash,
    x = 0,
    y = 0,
    extraValue = 0,
    swordLevel = 0,
  ) {
    super();
    this.id = type;
    const spec = EFFECT_SPECS[this.id];
    let objectSheet: BitmapData = null!;
    let rowX = 0;
    let isSmooth = false;
    this.tileSize = spec?.tileSize ?? 64;
    this.animSpeed = spec?.animSpeed ?? 2;
    this.lifeLimit = spec?.lifeLimit ?? 0;
    this.fadeRate = spec?.fadeRate ?? 5;
    if (spec !== undefined) {
      rowX = spec.rowX;
      isSmooth = spec.smoothing === true;
      objectSheet =
        spec.sheet !== null
          ? new spec.sheet()
          : new TRAIL_SHEETS[swordLevel === 0 ? 0 : 1]();
      this.frameLimit = spec.frameLimit;
      this.moveSpeed = spec.moveSpeed ?? 0;
      if (spec.scaleX !== undefined) {
        this.scaleX = spec.scaleX;
      }
      if (spec.scaleY !== undefined) {
        this.scaleY = spec.scaleY;
      }
      if (spec.targetScale !== undefined) {
        this.targetScale = spec.targetScale;
        this.scaleX = this.targetScale;
        this.scaleY = this.targetScale;
      }
      if (spec.rotation !== undefined) {
        this.rotation = spec.rotation;
      }
      if (spec.alpha !== undefined) {
        this.alpha = spec.alpha;
      }
      if (spec.additive === true) {
        this.blendMode = "add";
      }
      if (spec.startHidden === true) {
        this.alpha = 0;
        this.alphaCurrent = 0;
      }
      if (spec.randomRotation === true) {
        this.rotation = Math.ceil(Math.random() * 360) - 1;
      }
    }
    switch (this.id) {
      case EffectId.Dash: {
        this.targetAngle = extraValue - 90;
        this.rotation = this.targetAngle;
        const initialScaleD = 1 + swordLevel * 0.1;
        this.scaleX = initialScaleD;
        this.scaleY = initialScaleD;
        break;
      }
      case EffectId.SwordTrail:
      case EffectId.RobotTrail:
        this.frame = (extraValue * 2) | 0;
        break;
      case EffectId.AxeShockwave: {
        this.targetAngle = extraValue;
        this.rotation = this.targetAngle;
        const initialScaleSW = 0.4 + swordLevel * 0.15;
        this.scaleX = initialScaleSW;
        this.scaleY = initialScaleSW;
        this.setMovement();
        break;
      }
      case EffectId.GravityOrb:
        this.targetAngle = extraValue;
        this.setMovement();
        // three free steps on spawn; x/y are still 0 until the tail below, so
        // all these really do is start the fuse three frames in
        this.updateGravityBall();
        this.updateGravityBall();
        this.updateGravityBall();
        break;
      case EffectId.ExplosionSpike:
        this.targetAngle = extraValue;
        this.rotation = this.targetAngle;
        this.setMovement();
        break;
      case EffectId.CutSceneShine: {
        const c = new Color();
        c.setTint(16777215, 1);
        this.transform.colorTransform = c;
        break;
      }
      case EffectId.FortressExplosion: {
        const randomScale = Math.round(Math.random() * 95) + 140;
        this.scaleX = randomScale * 0.01;
        this.scaleY = this.scaleX;
        break;
      }
      case EffectId.XenosShard:
        this.targetAngle = extraValue;
        break;
      case EffectId.PortraitDistortion:
        this.life = Math.round(Math.random() * 50);
        break;
    }
    this.layer = new SpriteLayer(
      objectSheet,
      this.tileSize,
      this.tileSize,
      rowX,
    );
    const canvasBitmap = new Bitmap(this.layer.canvas);
    this.addChild(canvasBitmap);
    canvasBitmap.x = this.tileSize * -0.5;
    canvasBitmap.y = this.tileSize * -0.5;
    if (isSmooth) {
      canvasBitmap.smoothing = true;
    }
    this.x = x;
    this.y = y;
    this.drawFrame();
    if (swordLevel > 0) {
      const swordMatrix = new ColorMatrix();
      swordMatrix.adjustColor(0, 0, 0, SWORD_HUES[swordLevel]);
      this.filters = [new ColorMatrixFilter(swordMatrix)];
    }
  }

  updateDash(playerX: number, playerY: number): void {
    this.x = playerX;
    this.y = playerY;
  }

  updateTrail(playerX: number, playerY: number): void {
    if (this.action === 1) {
      this.visible = true;
      this.action = 0;
      if (this.id === EffectId.SwordTrail) {
        this.y = playerY - 3;
        this.x = playerX;
      } else {
        this.x = 0;
        this.y = 0;
        this.scaleY = this.targetScale;
      }
    }
    if (this.id === EffectId.RobotTrail) {
      this.x -= 45;
      this.scaleY -= 0.27;
    }
  }

  /** Advances the gravity orb; returns true on the frame it detonates. */
  updateGravityBall(): boolean {
    let detonated = false;
    if (this.fading === false) {
      this.x += this.xSpeed;
      this.y += this.ySpeed;
      ++this.life;
      if (this.life === 20) {
        this.kill();
        detonated = true;
      }
    }
    return detonated;
  }

  updateMove(): MoveState {
    if (this.moveSpeed > 0) {
      this.x += this.xSpeed;
      this.y += this.ySpeed;
    }
    this.scaleX += 0.027;
    this.scaleY += 0.027;
    return {
      xx: this.x,
      yy: this.y,
      angle: this.rotation,
      scale: this.scaleX,
      fading: this.fading,
    };
  }

  kill(): void {
    this.fading = true;
  }

  updateXenosShard(): void {
    if (this.moveSpeed < 15) {
      this.moveSpeed += 0.075;
    }
    this.setMovement();
    this.x += this.xSpeed;
    this.y += this.ySpeed;
  }

  /** Collects this pickup if the player is close enough; 0 means untouched. */
  updateItem(playerX = 0, playerY = 0): number {
    const pickup = ITEM_PICKUPS[this.id];
    if (pickup === undefined) {
      return 0;
    }
    const dx: number = playerX - this.x;
    const dy: number = playerY - this.y;
    if (Math.sqrt(dx * dx + dy * dy) >= pickup.dist) {
      return 0;
    }
    this.kill();
    return pickup.action;
  }

  /** Ticks animation and fade; returns 1 once the effect is fully faded. */
  updateAlpha(): number {
    let returnAction = 0;
    ++this.frameCounter;
    if (this.hitCD > 0) {
      --this.hitCD;
    }
    if (this.frameCounter >= this.animSpeed) {
      this.frameCounter = 0;
      ++this.frame;
      if (this.frame >= this.frameLimit) {
        this.frame = 0;
        // the trails park themselves until updateTrail repositions them
        if (
          this.id === EffectId.SwordTrail ||
          this.id === EffectId.RobotTrail
        ) {
          this.action = 1;
          this.visible = false;
        }
      }
      this.drawFrame();
    }
    if (this.lifeLimit > 0 && this.fading === false) {
      ++this.life;
      if (this.life >= this.lifeLimit) {
        this.kill();
      }
    }
    if (this.fading) {
      this.alphaCurrent -= this.fadeRate;
      this.alpha = this.alphaCurrent * 0.01;
      if (this.alphaCurrent === 0) {
        returnAction = 1;
      }
    }
    return returnAction;
  }

  updateCutScene(mode = 0): void {
    switch (mode) {
      case 0:
        if (this.fading === false && this.alphaCurrent < 100) {
          this.alphaCurrent += 1;
          this.alpha = this.alphaCurrent * 0.01;
        }
        this.x += this.xSpeed;
        if (this.hitCD === 0) {
          this.xSpeed = 0;
        }
        // the shine keeps swelling for as long as it is on screen
        if (this.id === EffectId.CutSceneShine) {
          this.scaleX += 0.05;
          this.scaleY += 0.05;
        }
        break;
      case 1:
        this.hitCD = 60;
        this.xSpeed = -3;
        break;
    }
  }

  /** Cuts to the two-frame glitch strip for four frames out of every 80. */
  updatePortraitDistort(): void {
    ++this.life;
    if (this.life === 76) {
      this.frame = 0;
      this.frameCounter = 0;
      this.animOffset = 4;
      this.frameLimit = 2;
      this.drawFrame();
    }
    if (this.life === 80) {
      this.frame = 0;
      this.frameCounter = 0;
      this.animOffset = 0;
      this.frameLimit = 4;
      this.life = 0;
      this.drawFrame();
    }
  }

  private setMovement(): void {
    const rad: number = this.targetAngle * (Math.PI / 180);
    this.xSpeed = this.moveSpeed * Math.cos(rad);
    this.ySpeed = this.moveSpeed * Math.sin(rad);
  }

  private drawFrame(): void {
    this.layer.draw(this.frame + this.animOffset);
  }
}
